console.log('VITE_APP_BASE_URL:', import.meta.env.VITE_APP_BASE_URL);
import "../sentry.js";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider } from "@react-oauth/google";

import ErrorBoundary from "./exceptionHandlers/ErrorBoundry.tsx";

import App from "./App.tsx";

import "./index.css";
import "./App.css";
import "react-calendar/dist/Calendar.css";
import "./styles/phone-input.css";

// Global error handlers for chunk loading and other unhandled errors
const handleGlobalError = (error: ErrorEvent) => {
  const isChunkLoadError =
    error.message?.includes("Loading chunk") ||
    error.message?.includes("Failed to fetch dynamically imported module") ||
    error.message?.includes("dynamically imported module");

  if (isChunkLoadError) {
    console.log("Chunk loading error detected (auto-reload temporarily disabled)");
    // window.location.reload(); // Temporarily disabled to debug
    return;
  }

  // Let other errors be handled by Sentry/ErrorBoundary
  console.error("Global error:", error);
};

const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
  const error = event.reason;
  const isChunkLoadError =
    error?.message?.includes("Loading chunk") ||
    error?.message?.includes("Failed to fetch dynamically imported module") ||
    error?.message?.includes("dynamically imported module") ||
    error?.toString?.().includes("Loading CSS chunk");

  if (isChunkLoadError) {
    console.log("Chunk loading promise rejection detected (auto-reload temporarily disabled)");
    // window.location.reload(); // Temporarily disabled to debug
    event.preventDefault(); // Prevent the error from being logged
    return;
  }

  // Let other promise rejections be handled normally
  console.error("Unhandled promise rejection:", error);
};

// Add global error handlers
window.addEventListener("error", handleGlobalError);
window.addEventListener("unhandledrejection", handleUnhandledRejection);

const queryClient = new QueryClient();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <GoogleOAuthProvider
      clientId={import.meta.env.VITE_GOOGLE_OAUTH2_CLIENT_ID}
    >
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  </StrictMode>
);
